import { ANNOTATION_EXCERPT_TEXT_MAX_LENGTH } from "@rakazo/contracts";
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import type { TextItem } from "pdfjs-dist/types/src/display/api";
import { clipUtf16 } from "./utf16";

/*
 * RAK-168: the preview of converted Office files and PDFs renders with pdf.js on a canvas, never
 * in an iframe. Safety comes from what we leave out: no annotation layer (links are not
 * clickable, forms do nothing), no XFA, no scripting (pdf.js only runs document scripts through
 * its viewer's sandbox, which we do not load). pdf.js 6 has no eval path any more, so the old
 * isEvalSupported switch no longer exists. The worker is bundled and served from our origin
 * (CSP script-src 'self').
 *
 * Runtime data pdf.js fetches on demand (image decoders for JPX/JBIG2 scans and their plain-JS
 * fallback, CMaps for CJK text, data of the 14 standard fonts, ICC profiles) comes from folders
 * the build ships next to the app (src/pdfjs-assets.ts), same origin, never a CDN. Without
 * 'wasm-unsafe-eval' in the CSP the Wasm decoders cannot start and pdf.js loads the JS fallback
 * from the same folder.
 */

type PdfJs = typeof import("pdfjs-dist");

let pdfjsPromise: Promise<PdfJs> | null = null;

/** Loads pdf.js once, on first use, with the worker from our own bundle. */
export function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= import("pdfjs-dist").then((pdfjs) => {
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    return pdfjs;
  });
  return pdfjsPromise;
}

/** Path below the app base where the build serves pdf.js runtime data of one pdf.js version. */
export function pdfjsAssetPath(version: string): string {
  return `pdfjs/${version}/`;
}

/** Folders of pdf.js runtime data, absolute on our origin, each with the trailing slash pdf.js wants. */
export function pdfjsAssetUrls(version: string, baseUrl: string, documentUrl: string) {
  const root = new URL(`${baseUrl}${pdfjsAssetPath(version)}`, documentUrl).href;
  return {
    wasmUrl: `${root}wasm/`,
    cMapUrl: `${root}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${root}standard_fonts/`,
    iccUrl: `${root}iccs/`,
  } as const;
}

/** Options every document opens with; exported so a test can pin them. */
export const PDF_DOCUMENT_OPTIONS = {
  enableXfa: false,
  // Fonts that are not embedded use a system font or the shipped standard font data.
  useSystemFonts: true,
  disableAutoFetch: true,
  stopAtErrors: false,
} as const;

/**
 * Opens PDF bytes with the runtime data shipped on our own origin (image decoders, CMaps, standard
 * fonts, ICC profiles); the bytes are copied because pdf.js hands the buffer to its worker.
 */
export async function openPdf(bytes: Uint8Array): Promise<PDFDocumentProxy> {
  const pdfjs = await loadPdfJs();
  return pdfjs.getDocument({
    ...PDF_DOCUMENT_OPTIONS,
    ...pdfjsAssetUrls(pdfjs.version, import.meta.env.BASE_URL, document.baseURI),
    data: bytes.slice(),
  }).promise;
}

/** Size of one page in PDF points at scale 1, rotation applied. */
export type PdfPageSize = { width: number; height: number };

/** Reads the size of every page (PDF points), in page order. */
export async function readPageSizes(doc: PDFDocumentProxy): Promise<PdfPageSize[]> {
  const sizes: PdfPageSize[] = [];
  for (let n = 1; n <= doc.numPages; n += 1) {
    const page = await doc.getPage(n);
    const viewport = page.getViewport({ scale: 1 });
    sizes.push({ width: viewport.width, height: viewport.height });
  }
  return sizes;
}

type PdfJsAnnotationModes = Pick<PdfJs, "AnnotationMode">;

/**
 * Draws a page into a canvas at a CSS width; the backing store follows the device pixel ratio
 * so text stays sharp. Annotations are drawn as part of the page (form values, stamps, signature
 * images belong to what the reader sees and to the excerpt for the bot), but only into the
 * canvas: no annotation layer, no form fields, links not clickable. The forms mode would leave
 * form fields out for an HTML layer we do not build.
 */
export function renderPage(
  pdfjs: PdfJsAnnotationModes,
  page: PDFPageProxy,
  canvas: HTMLCanvasElement,
  cssWidth: number,
  pixelRatio = globalThis.devicePixelRatio || 1,
): RenderTask {
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: (cssWidth / base.width) * pixelRatio });
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  return page.render({
    canvas,
    viewport,
    annotationMode: pdfjs.AnnotationMode.ENABLE,
  });
}

/** A page draw in progress; cancel() stops it so the canvas is free for the next one. */
export type PageDrawTask = { promise: Promise<void>; cancel: () => void };

/** Rejection of a draw that was cancelled before it started; no failure, shows no error. */
export class PageDrawCancelled extends Error {
  /** Creates the rejection with a fixed message and name, so callers can tell it from real failures. */
  constructor() {
    super("Page draw cancelled");
    this.name = "PageDrawCancelled";
  }
}

/** True for our own cancellation and for pdf.js' RenderingCancelledException. */
export function isDrawCancelled(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === "PageDrawCancelled" || name === "RenderingCancelledException";
}

/**
 * Starts drawing a page into a canvas. pdf.js refuses a second render into a canvas that is
 * still in use, so the caller cancels the previous task before it starts the next one. The check
 * for cancellation and the start of the render run in one step, so a cancelled draw never
 * touches the canvas.
 */
export function startPageDraw(
  getPage: () => Promise<PDFPageProxy>,
  canvas: HTMLCanvasElement,
  cssWidth: number,
  load: () => Promise<PdfJsAnnotationModes> = loadPdfJs,
): PageDrawTask {
  let cancelled = false;
  let task: RenderTask | null = null;
  const promise = (async () => {
    const [pdfjs, page] = await Promise.all([load(), getPage()]);
    if (cancelled) throw new PageDrawCancelled();
    task = renderPage(pdfjs, page, canvas, cssWidth);
    await task.promise;
  })();
  return {
    promise,
    cancel: () => {
      cancelled = true;
      task?.cancel();
    },
  };
}

/** A rectangle relative to the page, 0..1, origin top left. */
export type RelativeRect = { x0: number; y0: number; x1: number; y1: number };

type TextItemLike = { str: string; transform: number[]; width: number; height: number };

/** Maps a point of PDF user space to the page, relative 0..1 with origin top left. */
export type ToRelative = (x: number, y: number) => [number, number];

/**
 * Picks the text whose centre lies inside a page rectangle, in content-stream order, and caps
 * it at the excerpt limit of the contract, counted in UTF-16 units like the schema.
 */
export function textInRect(
  items: readonly TextItemLike[],
  toRelative: ToRelative,
  rect: RelativeRect,
  limit = ANNOTATION_EXCERPT_TEXT_MAX_LENGTH,
): string {
  const parts: string[] = [];
  for (const item of items) {
    if (!item.str.trim()) continue;
    const e = item.transform[4] ?? 0;
    const f = item.transform[5] ?? 0;
    // The item box starts at its baseline in user space.
    const [cx, cy] = toRelative(e + item.width / 2, f + item.height / 2);
    if (cx >= rect.x0 && cx <= rect.x1 && cy >= rect.y0 && cy <= rect.y1) parts.push(item.str);
  }
  const text = parts.join(" ").replace(/\s+/g, " ").trim();
  return clipUtf16(text, limit);
}

/** Text under a page rectangle, read from the page's text content (rotation and crop box applied). */
export async function readTextInRect(page: PDFPageProxy, rect: RelativeRect): Promise<string> {
  const viewport = page.getViewport({ scale: 1 });
  const content = await page.getTextContent();
  const items = content.items.filter((item): item is TextItem => "str" in item);
  const toRelative: ToRelative = (x, y) => {
    const [vx, vy] = viewport.convertToViewportPoint(x, y);
    return [vx / viewport.width, vy / viewport.height];
  };
  return textInRect(items, toRelative, rect);
}
